import z from 'zod'
import {
  type CalendarEventData,
  type CalendarEventDataWithDetails,
  CompanyIdSchema,
  EventTypeSchema,
  isDefined,
  isEmpty,
  isNotDefined,
  isNotEmpty,
  TeamIdSchema,
  WorkflowIdSchema,
  type WorkflowStageId,
  WorkflowStageIdSchema,
  type WorkflowActionId,
  WorkflowActionIdSchema,
} from '@terros/common'
import { AssignmentStateSchema, eventMatchesStringFilter } from '../event'

const WorkflowFilterBySchema = z.enum(['stage', 'action'])
export type WorkflowFilterBy = z.infer<typeof WorkflowFilterBySchema>

const CalendarClientFilterSchema = z.object({
  workflowId: WorkflowIdSchema.optional(),
  stageIds: z.array(WorkflowStageIdSchema).optional(),
  actionIds: z.array(WorkflowActionIdSchema).optional(),
  filterBy: WorkflowFilterBySchema.optional(),
  statuses: z.array(z.string()).meta({
    description: 'both the outcomeId and name for each outcome to compare to outcomeId and reasonNotOccurred',
  }),
  excludeStatuses: z.boolean().optional(),
  hasUltilityBill: z.boolean().optional(),
  hasCustomerConfirmation: z.boolean().optional(),
  assignmentState: AssignmentStateSchema,
  search: z.string().optional(),
})
export type CalendarClientFilter = z.infer<typeof CalendarClientFilterSchema>

const CalendarFilterViewAsSchema = z.union([CompanyIdSchema, TeamIdSchema, z.literal('user')])
export type CalendarFilterViewAs = z.infer<typeof CalendarFilterViewAsSchema>
const CalendarViewTypeSchema = z.enum(['user', 'company', 'team'])
export type CalendarViewType = z.infer<typeof CalendarViewTypeSchema>
export const CalendarFiltersSchema = z.object({
  viewAs: z.array(CalendarFilterViewAsSchema),
  viewType: CalendarViewTypeSchema.optional(),
  selectedTeams: TeamIdSchema.array().optional(),
  eventType: EventTypeSchema.optional(),
  ...CalendarClientFilterSchema.shape,
})
export type CalendarFilters = z.infer<typeof CalendarFiltersSchema>

export function filterEvents(
  data: CalendarEventDataWithDetails[],
  filter: CalendarClientFilter
): CalendarEventDataWithDetails[] | undefined {
  if (!data) return undefined
  const { search, ...otherFilters } = filter

  return data.filter((event) => isEventVisible(event, otherFilters) && eventMatchesStringFilter(event, search ?? ''))
}

export function isEventVisible(
  event: Pick<
    CalendarEventDataWithDetails,
    'specialtyAttachments' | 'reasonNotOccurred' | 'attendeeId' | 'outcomeId' | 'account'
  >,
  filter: Omit<CalendarClientFilter, 'search'>
): boolean {
  const {
    statuses,
    excludeStatuses,
    hasUltilityBill,
    hasCustomerConfirmation,
    assignmentState,
    stageIds,
    actionIds,
    filterBy = 'action',
  } = filter
  if (isNotEmpty(statuses)) {
    const isMatch = eventMatchesStatusFilter(event, statuses)
    if (excludeStatuses && isMatch) return false
    if (!excludeStatuses && !isMatch) return false
  }
  if (filterBy === 'action' && isNotEmpty(actionIds)) {
    const isMatch = eventMatchesWorkflowActionFilter(event, actionIds)
    if (excludeStatuses && isMatch) return false
    if (!excludeStatuses && !isMatch) return false
  } else if (isNotEmpty(stageIds)) {
    const isMatch = eventMatchesWorkflowStageFilter(event, stageIds)
    if (excludeStatuses && isMatch) return false
    if (!excludeStatuses && !isMatch) return false
  }

  const hasBill = Boolean(event.specialtyAttachments?.utilityBill)
  const hasConfirmation = Boolean(event.specialtyAttachments?.customerVerification)

  if (hasUltilityBill && !hasBill) return false
  if (hasCustomerConfirmation && !hasConfirmation) return false

  if (assignmentState === 'Unassigned' && event.attendeeId) return false
  if (assignmentState === 'Assigned' && !event.attendeeId) return false

  return true
}

function eventMatchesWorkflowStageFilter(
  event: Pick<CalendarEventDataWithDetails, 'account'>,
  stageIds: WorkflowStageId[]
): boolean {
  if (isNotDefined(event.account)) return false
  return stageIds.includes(event.account.workflowStageId)
}

function eventMatchesWorkflowActionFilter(
  event: Pick<CalendarEventDataWithDetails, 'account'>,
  actionIds: WorkflowActionId[]
): boolean {
  if (isNotDefined(event.account) || isNotDefined(event.account.workflowActionId)) return false
  return actionIds.includes(event.account.workflowActionId)
}

function eventMatchesStatusFilter(
  event: Pick<CalendarEventData, 'reasonNotOccurred' | 'outcomeId'>,
  statuses: string[]
): boolean {
  if (isEmpty(statuses)) return true

  if (!event.reasonNotOccurred && !event.outcomeId) return false

  const matchesReason = isDefined(event.reasonNotOccurred) && statuses.includes(event.reasonNotOccurred)
  const matchesOutcome = isDefined(event.outcomeId) && statuses.includes(event.outcomeId)

  return matchesReason || matchesOutcome
}
